import { useTranslation } from "react-i18next";
import { formatDate, formatList, formatNumber } from "@/i18n/format";

const unit = (value: number, kind: "hour" | "minute") =>
  formatNumber(value, { style: "unit", unit: kind, unitDisplay: "long" });

import type { BusyBlock, CalendarPerson, Slot } from "./calendarModel";
import { wallClock } from "./calendarModel";

const DAY: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" };
const LONG_DAY: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric" };
const TIME: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };

/** How the calendar cards say times, people and busy blocks. */
export function useCalendarText() {
  const { t } = useTranslation("chat", { keyPrefix: "calendar" });
  const time = (iso: string) => formatDate(wallClock(iso), TIME);
  const range = (start: string, end: string) => t("range", { start: time(start), end: time(end) });
  const when = (start: string, end: string, long = false) =>
    t("when", { day: formatDate(wallClock(start), long ? LONG_DAY : DAY), range: range(start, end) });
  const who = (email: string, organizer?: string | null) => (email === organizer ? t("you") : email);
  const status = (block: BusyBlock) => t(`status.${block.status === "tentative" ? "tentative" : "busy"}`);
  return {
    t,
    time,
    range,
    when,
    who,
    /** "2 hours", "90 minutes". */
    length: (minutes: number) => (minutes % 60 === 0 ? unit(minutes / 60, "hour") : unit(minutes, "minute")),
    hours: (minutes: number) => formatNumber(Math.round((minutes / 60) * 10) / 10),
    day: (date: string, long = false) => formatDate(wallClock(`${date}T00:00`), long ? LONG_DAY : DAY),
    block: (person: CalendarPerson, block: BusyBlock, organizer?: string | null) =>
      t("blockTitle", {
        who: who(person.email, organizer),
        what: block.subject ?? status(block),
        when: when(block.start, block.end),
      }),
    slot: (slot: Slot, organizer?: string | null) => {
      const base = when(slot.start, slot.end);
      if (slot.busy.length)
        return t("slotBusy", { when: base, people: formatList(slot.busy.map((e) => who(e, organizer))) });
      if (slot.tentative.length)
        return t("slotTentative", { when: base, people: formatList(slot.tentative.map((e) => who(e, organizer))) });
      return t("slotFree", { when: base });
    },
  };
}
